// ─── SommSync Auth ────────────────────────────────────────────────────────────
// Firebase Auth wiring for all three roles.
//
//   super user       email + password, every restaurant
//   restaurant owner email + password, their own restaurant(s)
//   floor manager    6-digit PIN, exchanged server-side for a custom token
//
// The PIN is not a second auth system. floorManagerLogin verifies it and returns
// a Firebase custom token, so every role ends up holding an ordinary ID token and
// every endpoint does the same check.
//
// These config values are not secrets - they identify the project and ship in
// every Firebase web app's bundle. Security comes from Auth and database rules.

import { useEffect, useState } from 'react';
import { initializeApp } from 'firebase/app';
import {
  getAuth,
  signInWithEmailAndPassword,
  signInWithCustomToken,
  signOut as fbSignOut,
  onIdTokenChanged,
  browserLocalPersistence,
  setPersistence,
} from 'firebase/auth';

const firebaseConfig = {
  apiKey: "AIzaSyBO4i3KkGPzon38_27-c6zV3xC1kXgNdGs",
  authDomain: "sommsync-platform.firebaseapp.com",
  databaseURL: "https://sommsync-platform-default-rtdb.firebaseio.com",
  projectId: "sommsync-platform",
  storageBucket: "sommsync-platform.firebasestorage.app",
  messagingSenderId: "892378277835",
  appId: "1:892378277835:web:029c2b3f50716dfa56384e",
};

const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);

// Tablets sit on a bar for weeks between restarts. Local persistence means a
// floor manager signs in once rather than every time the kiosk browser reloads.
setPersistence(auth, browserLocalPersistence).catch(() => {});

const BASE = 'https://us-central1-sommsync-platform.cloudfunctions.net';

// ─── Token ────────────────────────────────────────────────────────────────────
// Cached so the common path is synchronous-ish, but always refreshed through the
// SDK, which handles expiry. Never read the cache directly for a request.

let cachedToken = null;
let cachedClaims = null;

onIdTokenChanged(auth, async (user) => {
  if (!user) { cachedToken = null; cachedClaims = null; return; }
  try {
    const res = await user.getIdTokenResult();
    cachedToken = res.token;
    cachedClaims = { role: res.claims.role || null, restaurantIds: res.claims.restaurantIds || [] };
  } catch {
    cachedToken = null; cachedClaims = null;
  }
});

export async function getToken() {
  const user = auth.currentUser;
  if (!user) return null;
  try { return await user.getIdToken(); } catch { return null; }
}

export function getClaims() { return cachedClaims; }

// Adds the bearer token when signed in and leaves the request alone when not.
// Guest endpoints must keep working for someone who has never signed in.
export async function authFetch(url, options = {}) {
  const token = await getToken();
  if (!token) return fetch(url, options);
  return fetch(url, {
    ...options,
    headers: { ...(options.headers || {}), Authorization: `Bearer ${token}` },
  });
}

// ─── Sign in ──────────────────────────────────────────────────────────────────

export async function signInEmail(email, password) {
  const cred = await signInWithEmailAndPassword(auth, email.trim(), password);
  const res = await cred.user.getIdTokenResult();
  return { role: res.claims.role || null, restaurantIds: res.claims.restaurantIds || [] };
}

export async function signInFloorPin(restaurantId, pin) {
  const res = await fetch(`${BASE}/floorManagerLogin`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ restaurantId, pin }),
  });
  const data = await res.json();
  if (!res.ok || !data.customToken) {
    const err = new Error(data.error || 'Sign-in failed');
    err.retryAfterSeconds = data.retryAfterSeconds || null;
    err.attemptsBeforeWait = data.attemptsBeforeWait;
    err.status = res.status;
    throw err;
  }
  await signInWithCustomToken(auth, data.customToken);
  return { role: 'floor', restaurantIds: [restaurantId] };
}

export async function signOut() {
  cachedToken = null; cachedClaims = null;
  await fbSignOut(auth);
}

export function friendlyAuthError(e) {
  const code = e && e.code ? String(e.code) : '';
  if (code.includes('invalid-credential') || code.includes('wrong-password') || code.includes('user-not-found')) {
    return 'Email or password is incorrect.';
  }
  if (code.includes('too-many-requests')) return 'Too many attempts. Try again shortly.';
  if (code.includes('network-request-failed')) return 'Network problem — check your connection.';
  if (code.includes('user-disabled')) return 'This account has been disabled.';
  return (e && e.message) || 'Sign-in failed.';
}

// ─── Cross-origin handoff ─────────────────────────────────────────────────────
// A session at getsommsync.com does not carry to {id}.getsommsync.com. The
// company site mints a single-use nonce bound to the signed-in uid; the
// subdomain redeems it for a custom token.

export async function mintHandoff(restaurantId) {
  const res = await authFetch(`${BASE}/mintHandoff`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ restaurantId }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Could not start sign-in');
  return data.nonce;
}

export async function goToRestaurant(restaurantId) {
  const nonce = await mintHandoff(restaurantId);
  window.location.href = `https://${restaurantId}.getsommsync.com/?h=${nonce}`;
}

// Called once on app load. If the URL carries a handoff nonce, exchange it for a
// session and strip it from the address bar so it does not sit in history or
// leak through a referrer.
export async function redeemHandoffFromUrl() {
  const params = new URLSearchParams(window.location.search);
  const nonce = params.get('h');
  if (!nonce) return null;

  params.delete('h');
  const clean = window.location.pathname + (params.toString() ? `?${params}` : '');
  window.history.replaceState({}, '', clean);

  try {
    const res = await fetch(`${BASE}/redeemHandoff`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ nonce }),
    });
    const data = await res.json();
    if (!res.ok || !data.customToken) return { error: data.error || 'Sign-in link failed' };
    await signInWithCustomToken(auth, data.customToken);
    return { ok: true };
  } catch (e) {
    return { error: 'Sign-in failed' };
  }
}

// ─── React hook ───────────────────────────────────────────────────────────────
// `loading` is true until Firebase has restored any persisted session. Rendering
// a sign-in screen before that resolves makes an already-signed-in manager think
// they were logged out.

export function useAuthUser() {
  const [state, setState] = useState({ user: null, claims: null, loading: true });

  useEffect(() => {
    return onIdTokenChanged(auth, async (user) => {
      if (!user) return setState({ user: null, claims: null, loading: false });
      try {
        const res = await user.getIdTokenResult();
        setState({
          user,
          claims: { role: res.claims.role || null, restaurantIds: res.claims.restaurantIds || [] },
          loading: false,
        });
      } catch {
        setState({ user, claims: null, loading: false });
      }
    });
  }, []);

  return state;
}

// Does this caller act at `level` for `restaurantId`? Mirrors requireAuth on the
// server. UI convenience only — the server decides.
const ROLE_RANK = { floor: 1, owner: 2, super: 3 };
export function can(claims, level, restaurantId = null) {
  if (!claims || !claims.role) return false;
  if ((ROLE_RANK[claims.role] || 0) < (ROLE_RANK[level] || 99)) return false;
  if (claims.role === 'super') return true;
  if (restaurantId && !(claims.restaurantIds || []).includes(restaurantId)) return false;
  return true;
}
